import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { PositionCatalog } from "@/services/backend/htz/position-catalog/types/position-catalog";
import { Offer } from "@/services/backend/htz/offers/types/offer";
import { useHtzOfferPutMutation } from "@/services/backend/htz/offers/api/offer-api";
import { useDebouncedMutation } from "@/shared/lib/debounce/debounce";
import {
  rtkErrIsValidationError,
  ValidationError,
} from "@/shared/lib/errors/validation-error";
import { AirHandlingUnit } from "@/services/backend/htz/ahu/types/air-handling-unit";
import { rtkErrIsErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { useRefEffect } from "@/shared/hooks/use-ref-effect";

interface OfferContextInterface {
  offer: Offer;
  onOfferChange: (offer: Offer) => void;
  validationError: ValidationError | null;
  setValidationError: (err: ValidationError | null) => void;
  airHandlingUnits: AirHandlingUnit[];
  positionCatalog: PositionCatalog;
}

export const OfferContext = createContext<OfferContextInterface>({
  offer: {} as Offer,
  onOfferChange: () => null,
  validationError: null,
  setValidationError: () => null,
  positionCatalog: {} as PositionCatalog,
  airHandlingUnits: [],
});

export function useOfferContext() {
  return useContext(OfferContext);
}

export function OfferContextProvider({
  children,
  offer: propOffer,
  catalog: propCatalog,
  airHandlingUnits: propAirHandlingUnits,
}: {
  children: ReactNode;
  offer: Offer;
  catalog: PositionCatalog;
  airHandlingUnits: AirHandlingUnit[];
}) {
  // idea would be to use index db here for offline availability
  const [offer, setOffer] = useState<Offer>();
  const [catalog, setCatalog] = useState<PositionCatalog>();
  const [airHandlingUnits, setAirHandlingUnits] = useState<AirHandlingUnit[]>();
  const [validationError, setValidationError] =
    useState<ValidationError | null>(null);

  // after vector clocks are added, they can be used to
  // update the offer here.
  if (!offer || offer.id !== propOffer.id) {
    setOffer(propOffer);
  }

  // Once the offer is festgeschrieben on the server, force a resync so the
  // local buffer reflects the new immutability state and the freshly assigned
  // document number — local edits are no longer possible past this point.
  useRefEffect(propOffer.immutable, () => {
    setOffer(propOffer);
  });

  // Neither, the catalog nor the air handling units are mutated within the
  // offer context. Any value coming is must be an updated server value.
  if (!catalog || catalog !== propCatalog) {
    setCatalog(propCatalog);
  }
  if (!airHandlingUnits || airHandlingUnits !== propAirHandlingUnits) {
    setAirHandlingUnits(propAirHandlingUnits);
  }

  const onOfferChange = (changedOffer: Offer) => {
    setOffer(changedOffer);
  };

  const value = useMemo(
    () => ({
      offer: offer!,
      onOfferChange,
      validationError,
      setValidationError,
      airHandlingUnits: airHandlingUnits!,
      positionCatalog: catalog!,
    }),
    [airHandlingUnits, catalog, offer, validationError],
  );

  return (
    <OfferContext.Provider value={value}>
      <OfferUpdater>{children}</OfferUpdater>
    </OfferContext.Provider>
  );
}

function OfferUpdater({ children }: PropsWithChildren) {
  // this should only run when online
  const { offer, onOfferChange, setValidationError } = useOfferContext();
  const mutation = useHtzOfferPutMutation();
  const [, { error }] = mutation;

  useEffect(() => {
    if (rtkErrIsValidationError(error)) {
      setValidationError(error.data);
    }
    if (rtkErrIsErrDocumentNotReady(error)) {
      setValidationError(error.data);
    }
  }, [error, setValidationError]);

  // The offer context buffers the offer locally and surfaces validation errors
  // inline via setValidationError, so we keep the user's input on error rather
  // than resetting it (resetOnError: false). The true server value is not
  // available here; resetState mirrors the buffered offer.
  useDebouncedMutation(
    offer,
    (value) => onOfferChange(value instanceof Function ? value(offer) : value),
    mutation,
    offer,
    { skip: offer.immutable, resetOnError: false },
  );

  return children;
}
